import { assumeType } from 'hearts-engine';

import { TWEEN_DURATION, TWEEN_EASING } from '@/GameUI/Constants';
import type { Layout } from '@/GameUI/Definitions';
import Layouts from '@/utilities/Layouts';
import type Card from '@/GameUI/Sprites/Card';

const ONE_DEGREE_IN_RADIANS = 0.01745329252;
const CARD_WIDTH = 200;

type LayoutObject = (
	Phaser.GameObjects.Image |
	Phaser.GameObjects.TileSprite |
	Phaser.GameObjects.Container |
	Phaser.GameObjects.Graphics |
	Phaser.GameObjects.Text
) & {
	targetX?: number;
	targetY?: number;
	targetRotation?: number;
};

class LayoutManagerBase {
	scene: Phaser.Scene;
	private layouts: Layout[];

	constructor(scene?: Phaser.Scene) {
		this.layouts = Layouts;
		this.scene = scene;
	}

	setLayouts(layouts: Layout[]) {
		this.layouts = layouts;
	}

	updateTween(scene: Phaser.Scene, object: LayoutObject): boolean {
		let hasChanged = false;

		if (!object) {
			return false;
		}

		const hasTargetX = typeof object?.targetX !== 'undefined';
		const hasTargetY = typeof object?.targetY !== 'undefined';
		const hasTargetRotation = typeof object.targetRotation !== 'undefined';

		//snap in place if close enough
		if (hasTargetX && Math.abs(object.x - object.targetX) < 5) {
			object.x = object.targetX;
		}

		if (hasTargetY && Math.abs(object.y - object.targetY) < 5) {
			object.y = object.targetY;
		}

		//add tween if not at target
		if (
			(hasTargetX && object.x !== object.targetX) ||
			(hasTargetY && object.y !== object.targetY) ||
			(hasTargetRotation && object.rotation !== object.targetRotation)
		) {
			hasChanged = true;

			//delete old tween
			if (scene) {
				const oldTweens = scene.tweens.getTweensOf(object);

				for (let i = 0; i < oldTweens.length; ++i) {
					const oldTween = oldTweens[i];
					scene.tweens.remove(oldTween);
				}

				const tweenArgs = {
					targets: object,
					ease: TWEEN_EASING,
					duration: TWEEN_DURATION,
				} as Phaser.Types.Tweens.TweenBuilderConfig;

				if (hasTargetX) {
					tweenArgs.x = {
						from: object.x,
						to: object.targetX,
					};
				}

				if (hasTargetY) {
					tweenArgs.y = {
						from: object.y,
						to: object.targetY,
					};
				}

				scene.tweens.add(tweenArgs);
			} else {
				if (hasTargetX) {
					object.x = object.targetX;
				}

				if (hasTargetY) {
					object.y = object.targetY;
				}
			}
		}

		return hasChanged;
	}

	updateTweens(scene: Phaser.Scene, cards: Card[]): boolean {
		let hasChanged = false;

		for (const card of cards) {
			if (this.updateTween(scene, card)) {
				hasChanged = true;
			}
		}

		return hasChanged;
	}

	transformCordinates(width: number, height: number) {
		//loop through each layout item
		for (const layout of this.layouts) {
			//update x and y coordinates
			if (layout.xCenterOffset != null) {
				layout.x = width / 2 + layout.xCenterOffset;
			}

			if (layout.yCenterOffset != null) {
				layout.y = height / 2 + layout.yCenterOffset;
			}

			if (layout.left != null) {
				layout.x = layout.left;
			}

			if (layout.right != null) {
				layout.x = width - layout.right;
			}

			if (layout.top != null) {
				layout.y = layout.top;
			}

			if (layout.bottom != null) {
				layout.y = height - layout.bottom;
			}

			if (layout.rotation == null) {
				layout.rotation = 0;
			}

			if (layout.widthPercent != null) {
				layout.width = width * layout.widthPercent / 100;
			}

			if (layout.heightPercent != null) {
				layout.height = height * layout.heightPercent / 100;
			}
		}
	}

	updateProperties(gameObject: LayoutObject, layoutName?: string, scene: Phaser.Scene = this.scene) {

		if (!gameObject) {
			return;
		}

		//get Name of GameObject
		if (!layoutName && gameObject.name) {
			layoutName = gameObject.name;
		}

		//get layout
		const layout = this.getLayout(layoutName);

		if (!layout) {
			console.warn(`Could not find layout "${layoutName}" in layouts`, this.layouts);
			return;
		}

		//update properties
		if (layout.x !== null) {
			gameObject.targetX = layout.x;
		}

		if (layout.y !== null) {
			gameObject.targetY = layout.y;
		}

		if (layout.z !== null && gameObject.setDepth) {
			gameObject.setDepth(layout.z || 0);
		}

		if (layout.rotation != null) {
			gameObject.angle = layout.rotation;
		}

		if (layout.width != null) {
			if (typeof (gameObject as Phaser.GameObjects.Image).displayWidth !== 'undefined') {
				assumeType<Phaser.GameObjects.Image>(gameObject);
				gameObject.displayWidth = layout.width;
			}
		}

		if (layout.height != null) {
			if (typeof (gameObject as Phaser.GameObjects.Image).displayHeight !== 'undefined') {
				assumeType<Phaser.GameObjects.Image>(gameObject);
				gameObject.displayHeight = layout.height;
			}
		}

		this.updateTween(scene ?? this.scene, gameObject);

	}

	layoutCardPiles(cardUIs: Card[], scene: Phaser.Scene, playableCards: string[] = []): boolean {
		let hasCardMoved = false;

		for (const layout of this.layouts) {
			const { id: pileName, type: pileType } = layout;

			const pileCardUIs = cardUIs.filter((cardUI) => cardUI.pile === pileName);

			if (pileCardUIs.length === 0) {
				continue;
			}

			//todo: use layoutCardPileLocal for all hand piles. and myybe of other piles too
			//there is a lot of shared code between the two methods

			if (pileType === 'localHand') {
				const hasChangePileLocal = this.layoutCardPileLocal(pileName, pileCardUIs, scene, playableCards);
				hasCardMoved = hasCardMoved || hasChangePileLocal;
			} else {
				const hasChangePile = this.layoutCardPile(pileName, pileCardUIs, scene);
				hasCardMoved = hasCardMoved || hasChangePile;
			}
		}

		return hasCardMoved;

	}

	layoutCardPileLocal(layoutId: string, cards: Card[], scene: Phaser.Scene, playableCards: string[] = []): boolean {
		const layout = this.getLayout(layoutId);

		if (!layout || cards.length === 0) {
			return false;
		}

		const pileCount = cards.length;
		cards.sort((l, r) => l.compareTo(r));

		// Arc configuration
		const arcRadius = layout.arcRadius || 1100;
		let arcAngle = layout.arcAngle || 30;
		const useArc = layout.useArc === true;
		const radians = layout.rotation * ONE_DEGREE_IN_RADIANS;

		//total playable cards
		const totalPlayableCards = cards.filter((c) => playableCards.some((pc) => pc === c.id)).length;

		const lastCardId = cards[cards.length - 1].id;
		const isLastCardPlayable = playableCards.some((c) => c === lastCardId);
		const pileWidth = layout.pileWidth || 0;
		const totalPlayableCardsAdjusted = isLastCardPlayable ? totalPlayableCards - 1 : totalPlayableCards;
		const totalNonPlayableCards = pileCount - totalPlayableCards;
		const totalNonPlayableCardsAdjusted = !isLastCardPlayable ? totalNonPlayableCards - 1 : totalNonPlayableCards;
		const cardWidth = layout.width || CARD_WIDTH;

		// Give more space to playable cards
		const minSpread = 40;
		const maxSpread = 170;
		const availableWidth = pileWidth - cardWidth;
		let nonPlayableSpread = minSpread;

		//if all playable cards do not fit, find the max spread using what is available and maxing spread with a min of minSpread
		const possibleTotalPlayableCardsWidth = totalPlayableCardsAdjusted * maxSpread;
		if (possibleTotalPlayableCardsWidth < availableWidth && totalNonPlayableCardsAdjusted > 0) {
			const adjustedSpread = Math.max(minSpread, (availableWidth - possibleTotalPlayableCardsWidth) / totalNonPlayableCardsAdjusted);
			nonPlayableSpread = Math.min(adjustedSpread, maxSpread);
		}


		// Calculate spacing
		const nonPlayableWidthUsage = totalNonPlayableCardsAdjusted * nonPlayableSpread;
		const availableWidthForPlayableCards = availableWidth - nonPlayableWidthUsage;

		// Ensure playable cards get more generous spacing
		let playableSpread = maxSpread;
		if (availableWidthForPlayableCards > 0) {
			playableSpread = Math.min(maxSpread, availableWidthForPlayableCards / totalPlayableCardsAdjusted);
		}




		const playableWidthUsage = totalPlayableCardsAdjusted * playableSpread;
		const calculatedPileWidth = playableWidthUsage + nonPlayableWidthUsage;



		let currentX = 0;
		let lastCardIsPlayable = false;

		for (let cardIdx = 0; cardIdx < cards.length; ++cardIdx) {
			const card = cards[cardIdx];
			const isCardPlayable = playableCards.some((c) => c === card.id);

			if (layout.width != null) {
				card.displayWidth = layout.width;
			}

			if (layout.height != null) {
				card.displayHeight = layout.height;
			}

			if (layout.cardsVisible != null) {
				card.showFront = layout.cardsVisible;
			}

			card.setTint(isCardPlayable ? 0xFFFFFF : 0xAAAAAA);
			card.setVisible(true);
			card.setDepth((layout.z || 0) + cardIdx);

			// Calculate currentX for spacing
			const offsetX = lastCardIsPlayable ? playableSpread : nonPlayableSpread;
			currentX += offsetX;

			if (cardIdx === 0) {
				currentX = 0;
			}
			let finalX, finalY, cardRotation;



			// Linear positioning
			const offset = currentX - (calculatedPileWidth / 2);
			finalX = layout.x + (offset * Math.cos(radians));
			finalY = layout.y + (offset * Math.sin(radians));
			cardRotation = layout.rotation ?? 0;

			//if using arc, calculate the position based on the arc and offset
			if (useArc && pileCount > 1) {

				//adjust arcAngle if calculatedPileWidth is less than pileWidth
				let arcAngleAdjustment = (calculatedPileWidth / pileWidth) * arcAngle;
				arcAngleAdjustment = Math.min(arcAngle, arcAngleAdjustment);

				// Calculate the normalized position based on the offset
				const normalizedPosition = (offset / calculatedPileWidth);
				const angleInRadians = (normalizedPosition * arcAngleAdjustment) * ONE_DEGREE_IN_RADIANS;

				const arcX = Math.sin(angleInRadians) * arcRadius;
				const arcY = arcRadius - Math.cos(angleInRadians) * arcRadius;

				finalX = layout.x + (arcX * Math.cos(radians)) - (arcY * Math.sin(radians));
				finalY = layout.y + (arcX * Math.sin(radians)) + (arcY * Math.cos(radians));
				cardRotation = (layout.rotation || 0) + (normalizedPosition * arcAngleAdjustment);
			}





			card.targetX = finalX + (card.isSelected ? this.getSelectedOffsetX(layout, cardRotation) : 0);
			card.targetY = finalY + (card.isSelected ? this.getSelectedOffsetY(layout, cardRotation) : 0);
			card.angle = cardRotation;

			lastCardIsPlayable = isCardPlayable;
		}

		return this.updateTweens(scene, cards);
	}

	//Modifies the cards with the new x and y coordinates based on piles
	layoutCardPile(layoutId: string, cards: Card[], scene: Phaser.Scene): boolean {
		const layout = this.getLayout(layoutId);

		if (!layout || cards.length === 0) {
			return false;
		}

		const pileCount = cards.length;
		const cardWidth = layout.width || CARD_WIDTH;
		const pileWidth = layout.pileWidth || 0;

		cards.sort((l, r) => l.compareTo(r));
		const radians = layout.rotation * ONE_DEGREE_IN_RADIANS;

		// Arc configuration
		const arcRadius = layout.arcRadius || 800;
		const arcAngle = layout.arcAngle || 30;
		const useArc = layout.useArc === true;

		// Calculate spacing
		const totalCardsWidth = pileCount === 1 ? cardWidth : cardWidth + (pileCount - 1) * Math.min(cardWidth, (pileWidth - cardWidth) / (pileCount - 1));
		const actualPileWidth = Math.min(totalCardsWidth, pileWidth);

		let currentX = 0;

		for (let cardIdx = 0; cardIdx < cards.length; ++cardIdx) {
			const card = cards[cardIdx];

			if (layout.width != null) {
				card.displayWidth = layout.width;
			}

			if (layout.height != null) {
				card.displayHeight = layout.height;
			}

			if (layout.cardsVisible != null) {
				card.showFront = layout.cardsVisible;
			}

			card.clearTint();
			card.setVisible(true);
			card.setDepth((layout.z || 0) + cardIdx);

			if (pileCount === 1) {
				currentX = 0;
			} else {
				const remainingWidth = actualPileWidth - cardWidth - currentX;
				const remainingCards = pileCount - cardIdx;
				const offsetX = cardIdx === 0 ? 0 : remainingWidth / remainingCards;
				currentX += offsetX;
			}

			let finalX, finalY, cardRotation;

			if (useArc && pileCount > 1) {
				// Arc positioning
				const normalizedPosition = (cardIdx / (pileCount - 1)) - 0.5;
				const angleInRadians = (normalizedPosition * arcAngle) * ONE_DEGREE_IN_RADIANS;

				const arcX = Math.sin(angleInRadians) * arcRadius;
				const arcY = arcRadius - Math.cos(angleInRadians) * arcRadius;

				finalX = layout.x + (arcX * Math.cos(radians)) - (arcY * Math.sin(radians));
				finalY = layout.y + (arcX * Math.sin(radians)) + (arcY * Math.cos(radians));
				cardRotation = (layout.rotation || 0) + (normalizedPosition * arcAngle);
			} else {
				// Linear positioning
				const offset = (cardWidth / 2) + currentX - (actualPileWidth / 2);
				finalX = layout.x + (offset * Math.cos(radians));
				finalY = layout.y + (offset * Math.sin(radians));
				cardRotation = layout.rotation ?? 0;
			}

			card.targetX = finalX + (card.isSelected ? this.getSelectedOffsetX(layout, cardRotation) : 0);
			card.targetY = finalY + (card.isSelected ? this.getSelectedOffsetY(layout, cardRotation) : 0);
			card.angle = cardRotation;
		}

		return this.updateTweens(scene, cards);
	}

	getLayout(layoutId: string) {
		return this.layouts.find((l) => l.id === layoutId);
	}

	private getSelectedOffsetX(layout: Layout, cardRotation: number): number {
		const offsetX = layout.selectedOffestX ?? 0;
		const offsetY = layout.selectedOffestY ?? 0;

		if (offsetX === 0 && offsetY === 0) {
			return 0;
		}

		// Calculate the magnitude of the offset
		const offsetMagnitude = Math.sqrt(offsetX * offsetX + offsetY * offsetY);
		
		// Push the card "up" relative to its rotation (negative Y direction in card's local space)
		// Convert card rotation to radians and apply 90-degree offset to go "up"
		const rotationRadians = cardRotation * ONE_DEGREE_IN_RADIANS;
		const upDirectionRadians = rotationRadians - (Math.PI / 2); // -90 degrees for "up"
		
		return Math.cos(upDirectionRadians) * offsetMagnitude;
	}

	private getSelectedOffsetY(layout: Layout, cardRotation: number): number {
		const offsetX = layout.selectedOffestX ?? 0;
		const offsetY = layout.selectedOffestY ?? 0;

		if (offsetX === 0 && offsetY === 0) {
			return 0;
		}

		// Calculate the magnitude of the offset
		const offsetMagnitude = Math.sqrt(offsetX * offsetX + offsetY * offsetY);
		
		// Push the card "up" relative to its rotation (negative Y direction in card's local space)
		// Convert card rotation to radians and apply 90-degree offset to go "up"
		const rotationRadians = cardRotation * ONE_DEGREE_IN_RADIANS;
		const upDirectionRadians = rotationRadians - (Math.PI / 2); // -90 degrees for "up"
		
		return Math.sin(upDirectionRadians) * offsetMagnitude;
	}
}

const LayoutManager = new LayoutManagerBase();
export default LayoutManager;
